import { FC } from "react";
import { useTranslation } from "react-i18next";
import { useAppSelector, useAppDispatch } from "store/hooks";
import { getTestTypeValue } from "store/slices/test";
import { getTestTypeLocaleKey } from "utils/testType.utils";
import { useTheme } from "@mui/material/styles";
import { getPendingUpload, clearPendingUpload, clearResult } from "store/slices/imageRecognition";
import { uploadTestImages } from "services/imageUpload";
import { sentryLogException } from "services/sentryLogger";

import MDButton from "components/MDButton";
import ViImage from "assets/images/ViImage.svg?react";
import QuestionMark from "assets/images/QuestionMark.svg?react";
import ErrorIcon from "assets/images/ErrorIcon.svg?react";

import { makeStyles } from "tss-react/mui";
import { ResultActionType, UserActions } from "types/resultActions";
import { styles } from "./ModalsActions.styles";
import classNames from "classnames";
import { ReactElement } from "react";

interface StepProps {
  handleUserAction: (action: UserActions) => void;
  resultAction: ResultActionType;
  handleUpdateStep?: (step: number) => void;
  setResultAction?: (action: ResultActionType) => void;
}

interface ModalActionConfig {
  image: ReactElement;
  title: string;
  text?: string;
  actionButtonText?: string;
  actionButton?: () => void;
  cancelButtonText?: string;
  cancelButton?: () => void;
}

type ModalActionsMap = {
  [key in ResultActionType]: ModalActionConfig;
};

const ModalsActions: FC<StepProps> = ({ handleUserAction, resultAction, handleUpdateStep, setResultAction }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const testType = useAppSelector(getTestTypeValue);
  const testTypeLocaleKey = getTestTypeLocaleKey(testType);
  const pendingUpload = useAppSelector(getPendingUpload);
  const useStyles = makeStyles()(() => styles(theme));
  const { classes } = useStyles();

  const handleSelfReportingIssue = () => {
    // Show success message immediately

    // Clear previous resultAction from sessionStorage
    sessionStorage.removeItem('previousResultAction');

    // Trigger image upload in background with "error" result
    if (pendingUpload) {
      uploadTestImages(
        pendingUpload.images,
        pendingUpload.modelResult,
        pendingUpload.metadata,
        pendingUpload.testType,
        pendingUpload.sessionId,
        "error",
        pendingUpload.maxQualityImageIndex
      )
        .then(() => {
          console.log("ModalsActions: Image upload with error result completed successfully");
          // Clear pending upload data only after successful upload
          dispatch(clearPendingUpload());
        })
        .catch((error) => {
          console.error("ModalsActions: Image upload with error result failed:", error);
          sentryLogException({
            error,
            tags: {
              component: 'ModalsActions',
              category: 'imageUpload',
              testType,
            },
            extra: {
              resultAction: 'error',
              timestamp: new Date().toISOString(),
            },
          });
        });
    }
    
    handleUserAction("SELF_REPORTING_ISSUE");
  };

  const modalActionsMap: ModalActionsMap = {
    DONE: {
      image: <ViImage />,
      title: `${testTypeLocaleKey}:ModalsActions:doneTitle`,
    },
    REPORT: {
      image: <QuestionMark />,
      title: `${testTypeLocaleKey}:ModalsActions:questionTitle`,
      text: `${testTypeLocaleKey}:ModalsActions:questionText`,
      actionButtonText: `${testTypeLocaleKey}:ModalsActions:wrongResultButton`,
      actionButton: () => handleUserAction("WRONG_RESULT"),
      cancelButtonText: `${testTypeLocaleKey}:ModalsActions:noButton`,
      cancelButton: () => {
        // Go back to self-reporting screen (step 5) if coming from self-reporting
        if (handleUpdateStep) {
          handleUpdateStep(5);
        } else {
          console.log("noButton");
        }
      },
    },
    REPORT_ERROR: {
      image: <ErrorIcon />,
      title: `${testTypeLocaleKey}:ModalsActions:questionTitle`,
      text: `${testTypeLocaleKey}:ModalsActions:questionText`,
      actionButtonText: `${testTypeLocaleKey}:ModalsActions:wrongResultButton`,
      actionButton: () => handleUserAction("WRONG_RESULT"),
      cancelButtonText: `${testTypeLocaleKey}:ModalsActions:noButton`,
      cancelButton: () => {
        if (handleUpdateStep) {
          handleUpdateStep(5);
        } else {
          console.log("noButton");
        }
      },
    },
    SELF_REPORTING_ISSUE: {
      image: <ErrorIcon />,
      title: `SelfReportScreen:reportIssueTitle`,
      text: `SelfReportScreen:reportIssueText`,
      actionButtonText: `SelfReportScreen:submitReportIssue`,
      actionButton: handleSelfReportingIssue,
      cancelButton: () => {
        // If we came from INVALID_TEST, go back to INVALID_TEST, otherwise go to step 5
        const previousResultAction = sessionStorage.getItem('previousResultAction');
        if (previousResultAction === 'INVALID_TEST' && setResultAction) {
          setResultAction('INVALID_TEST');
        } else {
          handleUpdateStep?.(5);
        }
      },
      cancelButtonText: `${testTypeLocaleKey}:ModalsActions:noButton`,
    },
    INVALID_TEST: {
      image: <ErrorIcon />,
      title: `common:InvalidTestScreen:title`,
      text: `common:InvalidTestScreen:description`,
      actionButtonText: `common:InvalidTestScreen:retakeTestButton`,
      actionButton: () => {
        dispatch(clearResult()); // Reset state
        handleUpdateStep?.(1); // Go to instructions
      },
    },
  };

  return (
    resultAction ? (    
    <div className={classes.actionComponentContainer}>
      <div className={classes.actionBody}>
        <div className={classes.image}>
          {modalActionsMap[resultAction].image}
        </div>
        <div className={classes.title}>
          {t(modalActionsMap[resultAction].title)}
        </div>
        {modalActionsMap[resultAction].text ? (
          <div className={resultAction === "INVALID_TEST" ? classes.invalidTestText : classes.text}>
            {t(modalActionsMap[resultAction].text)}
          </div>
        ) : null}
      </div>
      <div
        className={classNames(
          classes.buttonsSection,
          modalActionsMap[resultAction].cancelButton ? classes.twoButtons : "",
          resultAction === "INVALID_TEST" ? classes.invalidTestButtons : ""
        )}
      >
        {modalActionsMap[resultAction].actionButton ? (
          <MDButton
            style={{ 
              width: resultAction === "INVALID_TEST" ? theme.width.full : theme.width.width75, 
              height: theme.spacing.spacing28 
            }}
            color={"primary"}
            variant="contained"
            type="submit"
            onClick={modalActionsMap[resultAction].actionButton}
          >
            {t(modalActionsMap[resultAction].actionButtonText)}
          </MDButton>
        ) : null}

        {modalActionsMap[resultAction].cancelButton ? (
          <MDButton
            style={{ minWidth: theme.width.width50, height: theme.spacing.spacing28 }}
            color={"primary"}
            variant="outlined"
            type="submit"
            onClick={modalActionsMap[resultAction].cancelButton}
          >
            {t(modalActionsMap[resultAction].cancelButtonText)}
          </MDButton>
        ) : null}
        {resultAction === "INVALID_TEST" ? (
          <div 
            className={classes.reportIssueLink}
            onClick={() => {
              // Show confirmation dialog (same as self-reporting screen)
              // Store previous resultAction to handle cancel properly
              if (setResultAction) {
                sessionStorage.setItem('previousResultAction', 'INVALID_TEST');
                setResultAction("SELF_REPORTING_ISSUE");
              }
            }}
          >
            {t("common:InvalidTestScreen:reportIssue")}
          </div>
        ) : null}
      </div>
    </div>
  ) : null)
};

export default ModalsActions;
